import {
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormGroupDirective,
  NgControl,
  NgForm
} from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { IonModal } from '@ionic/angular';
import { Nullable } from 'rt-shared-symbols';
import { BehaviorSubject, map, Observable, startWith, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  extractCombinedStatusesFromFormControl,
  FormControlCombinedStatuses,
  formControlCombinedStatusesAdapter
} from '../symbols/rt-inputs-control-status-adapter.symbols';
import { RtChoice, RtValidationErrorHandleStrategy } from '../symbols/rt-inputs-types.symbols';
import {
  rtFormsDefineInvalidityObservable,
  rtFormsOverwriteNgControlResetFunction
} from '../symbols/rt-inputs-validity.symbols';

@Directive({
  standalone: true
})
export abstract class RtInputsSelectorDirective<T> implements OnInit, OnDestroy, ControlValueAccessor {
  @ViewChild('modal', { static: true }) modal?: IonModal;
  @ViewChild('textInputElement') public textInputElement?: ElementRef<HTMLInputElement>;

  /** Placeholder for the input field. */
  @Input() placeholder: Nullable<string | undefined>;
  searchPlaceholder = $localize`Search...`;


  @Input() disabledOptions: string[];

  @Input() readOnly: boolean;

  /** Type of the input field. */
  @Input() inputType = 'string';

  /** Error handle strategy. */
  @Input() errorHandleStrategy: RtValidationErrorHandleStrategy = RtValidationErrorHandleStrategy.afterLeft;

  /** Relation between the error name imposed by Validators and the error message. */
  @Input() validationMessages: Nullable<{ [key: string]: string }>;

  @Input({ required: true }) label: string;

  @Input() set choices(items: RtChoice<any, string>[]) {
    this.choicesFiltered = items.reduce((acc, [id]) => {
      acc[id] = id;
      return acc;
    }, {} as { [id: string]: string });

    this._choices = items;
  }

  choicesDictionary(): { [id: string]: string } {
    return this.choices.reduce((acc, [id, name]) => {
      acc[id] = name;
      return acc;
    }, {} as { [id: string]: string });
  }

  get choices(): RtChoice<string, string>[] {
    return this._choices;
  }


  get emptyList(): boolean {
    return !Object.keys(this.choicesFiltered).length;
  }

  @Input() showSearchInput = true;

  @Input() showClearChoices = true;

  @Input() clearChoicesLabel = 'Clear choices';

  @Input() internalLabel: string;

  @Input() multiple = false;

  @Output() onSearch = new EventEmitter<string>();

  @Output() onSelect = new EventEmitter<string>();

  private _choices: RtChoice<string, string>[];


  public choicesFiltered: { [id: string]: string } = {};

  public focused = false;

  /** Triggered whether the value is changed. Will be overwritten by Angular forms. */
  public onChange?: (value: Nullable<string | T[]>) => void;

  /** Triggered whether the control is touched. Will be overwritten by Angular forms. */
  public onTouched?: () => void;

  /** Observable with the combined statuses of the form control. */
  public providedControlStatuses$: Observable<FormControlCombinedStatuses>;

  /** Indicates whether the validation should be displayed. */
  public shouldIndicateInvalidity$: Observable<boolean>;

  /** Form control for the input field. */
  public inputControl: FormControl<string | T[]>;

  public readonly searchFieldControl = new FormControl<string>('');

  /** Component subscriptions. Will be unsubscribed on component destroy. */
  public readonly subscription = new Subscription();

  /** Indicates whether the control is submitted. */
  public readonly isControlSubmitted$ = new BehaviorSubject(false);


  @HostBinding('class.disabled') protected isDisabled = false;

  constructor(
    protected renderer: Renderer2,
    public ngControl: NgControl,
    @Optional() public controlContainer: ControlContainer
  ) {
    // Use the `ngControl` to access the related form control.
    ngControl.valueAccessor = this;

    if (this.multiple) {
      this.inputControl = new FormControl<T[]>([]);
    } else {
      this.inputControl = new FormControl<string>('');
    }

    // Observe the form submit event if the related strategy is set.
    if (!controlContainer) {
      throw new Error(
        `The controlContainer is not found.
           It is impossible to show validation on form submit event if no form is provided.
           The ${RtInputsSelectorDirective.name} should be used within a <form>.
           If this is intended, change the errorHandleStrategy to the different value.`
      );
    }

    // Observe the form submit event to update the submitted state.
    if (controlContainer instanceof NgForm || controlContainer instanceof FormGroupDirective) {
      this.subscription.add(
        controlContainer.ngSubmit.subscribe(() => {
          this.isControlSubmitted$.next(true);
        })
      );
    }

  }

  ngOnInit(): void {

    // Overwrite the reset function of the control to reset the submitted state.
    rtFormsOverwriteNgControlResetFunction(this.ngControl, this.isControlSubmitted$);

    // Define the single event object based on the form control events.
    if (this.ngControl?.control?.events) {
      this.providedControlStatuses$ = this.ngControl.control.events.pipe(
        formControlCombinedStatusesAdapter(extractCombinedStatusesFromFormControl(this.ngControl.control))
      );
    }

    // Determine whether the control should be displayed as invalid.
    this.shouldIndicateInvalidity$ = rtFormsDefineInvalidityObservable(
      this.providedControlStatuses$,
      this.isControlSubmitted$,
      this.errorHandleStrategy
    );

    // Update the control statuses based on the provided statuses.
    this.subscription.add(
      this.providedControlStatuses$.subscribe(status => {
        const currentControlState = extractCombinedStatusesFromFormControl(this.inputControl);

        // If touched status is not synced, update the control status.
        if (status.touched !== currentControlState.touched) {
          if (status.touched) {
            this.inputControl.markAsTouched();
          } else {
            this.inputControl.markAsUntouched();
          }
        }

        // If pristine status is not synced, update the control status.
        if (status.pristine !== currentControlState.pristine) {
          if (status.pristine) {
            this.inputControl.markAsPristine();
          } else {
            this.inputControl.markAsDirty();
          }
        }
      })
    );

    // Notify the value accessor about the touch events in the input field.
    this.subscription.add(
      this.inputControl.events
        .pipe(
          // Cast the event to the Partial<FormControlCombinedStatuses> type as there is no way to cast it to the exact type.
          map(event => event as unknown as Partial<FormControlCombinedStatuses>),

          // Filter the statuses that are touched.
          filter(statuses => !!statuses?.touched)
        )
        .subscribe(() => {
          // Notify the value accessor about the touch events in the input field.
          if (this.onTouched) {
            this.onTouched();
          }
        })
    );

    // Notify the value accessor about the changes in the input field.
    this.subscription.add(
      this.inputControl.valueChanges.subscribe(value => {
        // Notify the value accessor about the changes in the input field.
        if (this.onChange) {
          this.onChange(value);
        }
      })
    );

    this.subscription.add(this.searchFieldControl.valueChanges.pipe(startWith(''))
      .subscribe({
        next: value => {
          this.choicesFiltered = this.choices?.filter(choice => choice[1].toLowerCase().includes(value.toLowerCase()))
            .reduce((acc, [id]) => {
              acc[id] = id;
              return acc;
            }, {} as { [id: string]: string });
          if (value) {
            this.onSearch.next(value);
          }
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Required by ControlValueAccessor.
   * Function for overriding the `onChange` class method.
   */
  public registerOnChange(fn: (value: any) => void): void {
    // Override the class method with provided function.
    this.onChange = fn;
  }

  /**
   * Required by ControlValueAccessor.
   * Function for overriding the `onTouched` class method.
   */
  public registerOnTouched(fn: () => void): void {
    // Override the class method with provided function.
    this.onTouched = fn;
  }

  /**
   * Required by ControlValueAccessor.
   * Describes how to update the form model when the value changes.
   */
  public writeValue(value: string | T[]): void {
    // Update the input value.
    if (this.inputControl) {
      this.inputControl.setValue(value, { emitEvent: false });
      if (this.readOnly) {
        this.inputControl.disable();
      }
    }
  }


  /**
   * Required by ControlValueAccessor.
   * Describes what actions should be taken when the control status changes.
   */
  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) {
      this.inputControl.disable({ emitEvent: false });
    } else {
      this.inputControl.enable({ emitEvent: false });
    }
    if (this.textInputElement) {
      this.renderer.setProperty(this.textInputElement.nativeElement, 'disabled', isDisabled);
    }
  }

  selectionChange($event: MatSelectChange<any>) {
    this.onSelect.next($event.value);
  }

}
